import React from 'react';
import PropTypes from 'prop-types';

// Character images - используем new URL() для Parcel
const fishProfessor = new URL('../assets/characters/fish-professor.webp', import.meta.url).href;
const crabEconomist = new URL('../assets/characters/crab-economist.webp', import.meta.url).href;
const octopusChef = new URL('../assets/characters/octopus-chef.webp', import.meta.url).href;
const crabDetective = new URL('../assets/characters/crab-detective.webp', import.meta.url).href;

/**
 * CharacterMessage - Reusable component for showing characters with messages
 *
 * Use cases:
 * - Empty states (empty cart, no orders, etc.)
 * - Error messages
 * - Success messages
 * - Loading states
 * - Informational hints
 *
 * Characters:
 * - fish-professor: Educational, guides, tutorials
 * - crab-economist: Economics, savings, benefits
 * - octopus-chef: Products, quality, promotions
 * - crab-detective: Errors, problems, troubleshooting
 */

const CHARACTER_MAP = {
  'fish-professor': {
    image: fishProfessor,
    name: 'Профессор Нимо',
    color: '#0EA5E9'
  },
  'crab-economist': {
    image: crabEconomist,
    name: 'Краб Крабс',
    color: '#10B981'
  },
  'octopus-chef': {
    image: octopusChef,
    name: 'Шеф Октавиус',
    color: '#F97316'
  },
  'crab-detective': {
    image: crabDetective,
    name: 'Детектив Клешни',
    color: '#EF4444'
  }
};

const CharacterMessage = ({
  character = 'fish-professor',
  title,
  message,
  action,
  actionText = 'Понятно',
  size = 'medium',
  showName = true
}) => {
  const characterData = CHARACTER_MAP[character];

  if (!characterData) {
    console.error(`Unknown character: ${character}`);
    return null;
  }

  const sizeClasses = {
    small: 'w-20 h-20',
    medium: 'w-32 h-32',
    large: 'w-48 h-48'
  };

  return (
    <div className="character-message flex flex-col items-center text-center p-6 max-w-md mx-auto">
      {/* Character Image */}
      <img
        src={characterData.image}
        alt={characterData.name}
        className={`${sizeClasses[size]} object-contain drop-shadow-2xl mb-4`}
        loading="lazy"
      />

      {/* Character Name */}
      {showName && (
        <p
          className="text-sm font-medium mb-2 opacity-70"
          style={{ color: characterData.color }}
        >
          {characterData.name}
        </p>
      )}

      {/* Title */}
      {title && (
        <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">
          {title}
        </h3>
      )}

      {/* Message */}
      {message && (
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
          {message}
        </p>
      )}

      {/* Action Button */}
      {action && (
        <button
          onClick={action}
          className="px-6 py-2 rounded-xl font-medium text-white transition-all hover:scale-105"
          style={{ backgroundColor: characterData.color }}
        >
          {actionText}
        </button>
      )}
    </div>
  );
};

CharacterMessage.propTypes = {
  character: PropTypes.oneOf(['fish-professor', 'crab-economist', 'octopus-chef', 'crab-detective']),
  title: PropTypes.string,
  message: PropTypes.string,
  action: PropTypes.func,
  actionText: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  showName: PropTypes.bool
};

export default CharacterMessage;
